<script lang="ts">
  import { CircleCheckIcon, SendIcon, ShoppingCartIcon } from "lucide-svelte";
  import { updateMeta, injectJsonLd } from "../../seo/meta";
  import { ROUTE_SEO } from "../../seo/routes";
  import { ORGANIZATION_SCHEMA, WORKSHOP_EVENT_SCHEMA, WORKSHOP_FAQ_SCHEMA, combineSchemas, getBreadcrumbSchema } from "../../seo/schemas";
  updateMeta(ROUTE_SEO["/workshops/dont-get-wrecked"]);
  injectJsonLd(combineSchemas(
    ORGANIZATION_SCHEMA,
    WORKSHOP_EVENT_SCHEMA,
    WORKSHOP_FAQ_SCHEMA,
    getBreadcrumbSchema([
      { name: "Home", url: "https://0xfire.com/" },
      { name: "Workshops", url: "https://0xfire.com/workshops/dont-get-wrecked" },
      { name: "Don't Get Wrecked", url: "https://0xfire.com/workshops/dont-get-wrecked" },
    ]),
  ));
</script>

<div class="min-h-screen bg-black text-gray-300 selection:bg-orange-900 selection:text-white">
  <div class="border-t border-gray-800"></div>
  <div class="text-center py-20 px-4">
    <p class="text-sm font-mono text-gray-600 uppercase tracking-[0.3em] mb-4">Workshop</p>
    <h1 class="text-4xl md:text-5xl font-black text-orange-500 tracking-tight mb-4">
      Don't Get Wrecked In Crypto
    </h1>

    <p class="text-lg text-gray-500 mt-4 max-w-xl mx-auto">
      A practical and brutally honest session for people who want to learn what to
      be afraid of in crypto
    </p>

    <p class="text-sm text-gray-600 mt-4 font-mono">
      Every Sunday in Berlin &middot; 2:30 PM
    </p>

    <div class="text-center my-8 flex flex-col sm:flex-row gap-4 justify-center items-center">
      <a
        href="https://forms.gle/LDP38K4pPyCVQM4x5"
        target="_blank"
        class="btn btn-lg bg-orange-600 hover:bg-orange-500 text-white border-none rounded-sm uppercase tracking-widest font-bold px-8 transition-colors"
      >
        <ShoppingCartIcon class="size-4" />
        Buy now for &euro;65
      </a>
      <a
        href="https://t.me/+zjZiIc5anlswYTMy"
        target="_blank"
        class="btn btn-lg bg-transparent text-gray-400 hover:text-white border border-gray-700 hover:border-orange-500/50 rounded-sm uppercase tracking-widest font-bold px-8 transition-all"
      >
        <SendIcon class="size-4" />Join Telegram
      </a>
    </div>

    <p class="text-xs text-gray-700 font-mono">
      Spend &euro;65 to save yourself from getting wrecked
    </p>

    <div class="flex justify-center mt-12">
      <div class="rounded-sm overflow-hidden border border-gray-800">
        <img
          src="https://0xfire.com/gone.webp"
          alt="Don't Get Wrecked"
          class="w-96"
        />
      </div>
    </div>
  </div>

  <div class="border-t border-gray-800"></div>

  <h2 class="text-[11px] font-mono text-center text-gray-600 mt-16 uppercase tracking-[0.4em]">Content</h2>

  <section class="max-w-5xl mx-auto px-6 py-16">
    <!-- Section 1 -->
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm mb-6">
      <div class="p-6">
        <h2 class="text-lg font-bold text-white mb-1">1. Why crypto trading is so risky?</h2>
        <p class="text-xs text-gray-600 font-mono mb-4">15 minutes</p>
        <div class="border-t border-gray-800 mb-4"></div>
        <ul class="space-y-3">
          <li class="flex items-start gap-2">
            <CircleCheckIcon class="size-4 text-orange-500 mt-0.5 shrink-0" />
            <span class="text-sm">How bots trade live</span>
          </li>
          <li class="flex items-start gap-2">
            <CircleCheckIcon class="size-4 text-orange-500 mt-0.5 shrink-0" />
            <span class="text-sm">Short demo of my bots trading live on Solana, ETH & Hyperliquid</span>
          </li>
          <li class="flex items-start gap-2">
            <CircleCheckIcon class="size-4 text-orange-500 mt-0.5 shrink-0" />
            <span class="text-sm">Why you need to automate your trading, even if you don't code</span>
          </li>
        </ul>
      </div>
    </div>

    <!-- Section 2 -->
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm mb-6">
      <div class="p-6">
        <h2 class="text-lg font-bold text-white mb-1">2. Where coins come from?</h2>
        <p class="text-xs text-gray-600 font-mono mb-4">15 minutes</p>
        <div class="border-t border-gray-800 mb-4"></div>
        <ul class="space-y-3">
          <li class="flex items-start gap-2">
            <CircleCheckIcon class="size-4 text-orange-500 mt-0.5 shrink-0" />
            <span class="text-sm">Back in the Day (2016-2019) - ICOs</span>
          </li>
          <li class="flex items-start gap-2">
            <CircleCheckIcon class="size-4 text-orange-500 mt-0.5 shrink-0" />
            <span class="text-sm">Collectables (2020-2023) - NFTs & Stake Economics</span>
          </li>
          <li class="flex items-start gap-2">
            <CircleCheckIcon class="size-4 text-orange-500 mt-0.5 shrink-0" />
            <span class="text-sm">Today (2025) - Meme Coins & Pump.fun</span>
          </li>
        </ul>
      </div>
    </div>

    <!-- Section 3 -->
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm mb-6">
      <div class="p-6">
        <h2 class="text-lg font-bold text-white mb-1">3. Who will get you wrecked?</h2>
        <p class="text-xs text-gray-600 font-mono mb-4">45 minutes</p>
        <div class="border-t border-gray-800 mb-4"></div>
        <div class="grid sm:grid-cols-2 gap-4">
          <div class="border border-gray-800 rounded-sm p-4 hover:border-orange-900/50 transition-colors bg-black/30">
            <CircleCheckIcon class="size-4 text-orange-500 mb-2" />
            <p class="text-sm font-bold text-white mb-2">Institutionals</p>
            <ul class="list-disc pl-5 text-xs text-gray-500 space-y-1">
              <li>Everything everywhere</li>
              <li>Insiders information</li>
              <li>Math not vibes</li>
            </ul>
          </div>
          <div class="border border-gray-800 rounded-sm p-4 hover:border-orange-900/50 transition-colors bg-black/30">
            <CircleCheckIcon class="size-4 text-orange-500 mb-2" />
            <p class="text-sm font-bold text-white mb-2">MEV Bots</p>
            <ul class="list-disc pl-5 text-xs text-gray-500 space-y-1">
              <li>Arbitrage</li>
              <li>Sandwich attack</li>
              <li>Liquidators</li>
            </ul>
          </div>
          <div class="border border-gray-800 rounded-sm p-4 hover:border-orange-900/50 transition-colors bg-black/30">
            <CircleCheckIcon class="size-4 text-orange-500 mb-2" />
            <p class="text-sm font-bold text-white mb-2">Pump & Dumpers</p>
            <ul class="list-disc pl-5 text-xs text-gray-500 space-y-1">
              <li>KOLs</li>
              <li>Telegram groups</li>
              <li>Twitter</li>
            </ul>
          </div>
          <div class="border border-gray-800 rounded-sm p-4 hover:border-orange-900/50 transition-colors bg-black/30">
            <CircleCheckIcon class="size-4 text-orange-500 mb-2" />
            <p class="text-sm font-bold text-white mb-2">Fake activity bots</p>
            <ul class="list-disc pl-5 text-xs text-gray-500 space-y-1">
              <li>Volume bots</li>
              <li>Sniper bots</li>
              <li>Bundlers bots</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 4 -->
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm mb-6">
      <div class="p-6">
        <h2 class="text-lg font-bold text-white mb-1">4. Crypto financial instruments</h2>
        <p class="text-xs text-gray-600 font-mono mb-4">45 minutes</p>
        <div class="border-t border-gray-800 mb-4"></div>
        <div class="grid sm:grid-cols-3 gap-4">
          <div class="border border-gray-800 rounded-sm p-4 hover:border-orange-900/50 transition-colors bg-black/30">
            <CircleCheckIcon class="size-4 text-orange-500 mb-2" />
            <p class="text-sm font-bold text-white mb-2">Lending protocols</p>
            <ul class="list-disc pl-5 text-xs text-gray-500 space-y-1">
              <li>How to lend</li>
              <li>How to borrow</li>
              <li>Risks & rewards</li>
            </ul>
          </div>
          <div class="border border-gray-800 rounded-sm p-4 hover:border-orange-900/50 transition-colors bg-black/30">
            <CircleCheckIcon class="size-4 text-orange-500 mb-2" />
            <p class="text-sm font-bold text-white mb-2">Providing liquidity</p>
            <ul class="list-disc pl-5 text-xs text-gray-500 space-y-1">
              <li>How to provide liquidity</li>
              <li>Impermanent loss</li>
              <li>Risks & rewards</li>
            </ul>
          </div>
          <div class="border border-gray-800 rounded-sm p-4 hover:border-orange-900/50 transition-colors bg-black/30">
            <CircleCheckIcon class="size-4 text-orange-500 mb-2" />
            <p class="text-sm font-bold text-white mb-2">Hyperliquid vaults</p>
            <ul class="list-disc pl-5 text-xs text-gray-500 space-y-1">
              <li>What are they</li>
              <li>How to use them</li>
              <li>Risks & rewards</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 5 -->
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm mb-6">
      <div class="p-6">
        <h2 class="text-lg font-bold text-white mb-1">5. How to build a strategy?</h2>
        <p class="text-xs text-gray-600 font-mono mb-4">60 minutes</p>
        <div class="border-t border-gray-800 mb-4"></div>
        <div class="grid sm:grid-cols-3 gap-4">
          <div class="border border-gray-800 rounded-sm p-4 hover:border-orange-900/50 transition-colors bg-black/30">
            <CircleCheckIcon class="size-4 text-orange-500 mb-2" />
            <p class="text-sm font-bold text-white mb-2">Hypothesis exploration</p>
            <ul class="list-disc pl-5 text-xs text-gray-500 space-y-1">
              <li>Where to look for ideas?</li>
              <li>How to rate them?</li>
              <li>When to start testing?</li>
            </ul>
          </div>
          <div class="border border-gray-800 rounded-sm p-4 hover:border-orange-900/50 transition-colors bg-black/30">
            <CircleCheckIcon class="size-4 text-orange-500 mb-2" />
            <p class="text-sm font-bold text-white mb-2">Strategy testing</p>
            <ul class="list-disc pl-5 text-xs text-gray-500 space-y-1">
              <li>How to backtest with TradingView candles</li>
              <li>How to setup paper trading accounts</li>
              <li>How to know if a strategy is profitable?</li>
            </ul>
          </div>
          <div class="border border-gray-800 rounded-sm p-4 hover:border-orange-900/50 transition-colors bg-black/30">
            <CircleCheckIcon class="size-4 text-orange-500 mb-2" />
            <p class="text-sm font-bold text-white mb-2">Strategy implementation</p>
            <ul class="list-disc pl-5 text-xs text-gray-500 space-y-1">
              <li>Showcase how to setup a simple quantconnect bot</li>
              <li>How to monitor your bot performance</li>
              <li>How to improve it?</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 6 -->
    <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm">
      <div class="p-6">
        <h2 class="text-lg font-bold text-white mb-1">6. Let's get wrecked!</h2>
        <p class="text-xs text-gray-600 font-mono mb-4">30 minutes</p>
        <div class="border-t border-gray-800 mb-4"></div>
        <div class="grid sm:grid-cols-3 gap-4">
          <div class="border border-gray-800 rounded-sm p-4 hover:border-orange-900/50 transition-colors bg-black/30">
            <CircleCheckIcon class="size-4 text-orange-500 mb-2" />
            <p class="text-sm font-bold text-white mb-2">We get a trade idea</p>
            <ul class="list-disc pl-5 text-xs text-gray-500 space-y-1">
              <li>Group discussion</li>
              <li>Present trade ideas</li>
              <li>We vote for the idea to wreck us</li>
            </ul>
          </div>
          <div class="border border-gray-800 rounded-sm p-4 hover:border-orange-900/50 transition-colors bg-black/30">
            <CircleCheckIcon class="size-4 text-orange-500 mb-2" />
            <p class="text-sm font-bold text-white mb-2">We execute the trade manually</p>
            <ul class="list-disc pl-5 text-xs text-gray-500 space-y-1">
              <li>Host shares screen</li>
              <li>Host executes the trade with $200</li>
              <li>We all get wrecked</li>
            </ul>
          </div>
          <div class="border border-gray-800 rounded-sm p-4 hover:border-orange-900/50 transition-colors bg-black/30">
            <CircleCheckIcon class="size-4 text-orange-500 mb-2" />
            <p class="text-sm font-bold text-white mb-2">Questions & answers</p>
            <ul class="list-disc pl-5 text-xs text-gray-500 space-y-1">
              <li>While we get wrecked, host answers questions</li>
              <li>At the end, we discuss the trade</li>
              <li>We all share our thoughts</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>

  <div class="border-t border-gray-800"></div>
  <h2 class="text-[11px] font-mono text-center text-gray-600 mt-16 uppercase tracking-[0.4em]">FAQs</h2>
  <section class="max-w-3xl mx-auto my-16 px-4">
    <div class="join join-vertical w-full space-y-2">
      <div class="collapse collapse-arrow join-item border border-gray-800 bg-gray-950/40 rounded-sm">
        <input type="radio" name="faq-accordion" checked={true} />
        <div class="collapse-title font-bold text-white">
          If you have profitable bots, why are you making these workshops?
        </div>
        <div class="collapse-content text-sm text-gray-400 leading-relaxed">
          <ul class="list-disc pl-6 space-y-2">
            <li>Money. I don't want to raise money from investors — my money's already busy working.</li>
            <li>Plus, there's a chance some of you might need my platform at some point.</li>
          </ul>
        </div>
      </div>

      <div class="collapse collapse-arrow join-item border border-gray-800 bg-gray-950/40 rounded-sm">
        <input type="radio" name="faq-accordion" />
        <div class="collapse-title font-bold text-white">
          How long have you been doing this?
        </div>
        <div class="collapse-content text-sm text-gray-400 leading-relaxed">
          <ul class="list-disc pl-6 space-y-2">
            <li>I've been building tech startups since 2015.</li>
            <li>Started with crypto in 2020.</li>
            <li>Been running serious (and successful) algorithmic trading since 2022.</li>
          </ul>
        </div>
      </div>

      <div class="collapse collapse-arrow join-item border border-gray-800 bg-gray-950/40 rounded-sm">
        <input type="radio" name="faq-accordion" />
        <div class="collapse-title font-bold text-white">
          How much money have you made with your bots?
        </div>
        <div class="collapse-content text-sm text-gray-400 leading-relaxed">
          <ul class="list-disc pl-6 space-y-2">
            <li>Enough to hire a 5 star senior developer full-time.</li>
            <li>Enough to not need a day job.</li>
            <li>Not enough (yet) to retire — still building the system to get there.</li>
          </ul>
        </div>
      </div>

      <div class="collapse collapse-arrow join-item border border-gray-800 bg-gray-950/40 rounded-sm">
        <input type="radio" name="faq-accordion" />
        <div class="collapse-title font-bold text-white">
          Will I learn how to make money with crypto in this workshop?
        </div>
        <div class="collapse-content text-sm text-gray-400 leading-relaxed">
          <ul class="list-disc pl-6 space-y-2">
            <li><strong>NO!</strong></li>
            <li>But you <em>will</em> learn how <strong>not</strong> to lose money — by seeing the real risks and schemes out there.</li>
            <li>If you want investment advice, please hire a financial advisor.</li>
          </ul>
        </div>
      </div>

      <div class="collapse collapse-arrow join-item border border-gray-800 bg-gray-950/40 rounded-sm">
        <input type="radio" name="faq-accordion" />
        <div class="collapse-title font-bold text-white">
          What will I get at the end of the workshop?
        </div>
        <div class="collapse-content text-sm text-gray-400 leading-relaxed">
          <ul class="list-disc pl-6 space-y-2">
            <li>You'll understand who you're trading against.</li>
            <li>You'll be scared (in a good way) — and that fear will become your best risk management tool.</li>
            <li>You'll have a step-by-step guide for discovering, developing, testing, and running your own strategy.</li>
          </ul>
        </div>
      </div>
    </div>
  </section>
</div>
